import { GetServerSideProps, GetServerSidePropsContext } from "next";
import { ParsedUrlQuery } from "querystring";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import getChannelConfigByPathName from "src/api/channel";
import { checkUnknownError } from "src/utils/errors";
import { LOGICAL_GROUP_CHANNEL_CODE } from "src/constants/logicalGroupChannelCode";
import { formatPathByLogicalGroupChannel } from "src/utils/url";
import { ChannelConfigType } from "src/types/channelConfig";
import { ProductCategoryType } from "src/types/productCategory";
import { omit } from "lodash";
import { withLayout } from "src/components/Layout/withLayout";
import withUserOrGuest from "src/components/Auth/withUserOrGuest";
import { getProductCategoryByCode } from "../../../api/productCategory";
import ProductTypes from "../../../types/productType";
import { Banner as BannerType } from "../../../types/banner";
import ProductCategoryPage, {
  ProductCategoryPagePropType,
} from "../../../components/ProductCategoryPage";

export interface CustomParams extends ParsedUrlQuery {
  channelPath: string;
  categoryCode: string;
}

export const getServerSideProps: GetServerSideProps<
  ProductCategoryPagePropType,
  CustomParams
> = async (context: GetServerSidePropsContext<CustomParams>) => {
  const categoryCode = context.params?.categoryCode;
  const channelPath = context.params?.channelPath;
  const { locale } = context;
  const query: Record<string, string> = omit(context.query, [
    "categoryCode",
    "channelPath",
  ]) as Record<string, string>;
  const urlQuery = new URLSearchParams(query).toString();
  let channelConfig: ChannelConfigType;
  let productCategory: ProductCategoryType;

  // check categoryCode must not empty
  if (!categoryCode || !channelPath) {
    return {
      notFound: true,
    };
  }

  try {
    channelConfig = await getChannelConfigByPathName(channelPath);
    if (channelConfig.logicalGroupChannelCode !== LOGICAL_GROUP_CHANNEL_CODE) {
      return {
        notFound: true,
      };
    }
  } catch (error) {
    const isUnknownError = checkUnknownError(error);
    if (!isUnknownError) {
      return {
        notFound: true,
      };
    }
    throw error;
  }

  try {
    productCategory = await getProductCategoryByCode(
      categoryCode,
      channelConfig.code
    );

    // redirect to externalLink or to go plan page
    if (productCategory.type === ProductTypes.external) {
      return {
        redirect: {
          destination: `${productCategory.externalLink}?${urlQuery}`,
          permanent: false,
        },
      };
    }
    if (productCategory.type === ProductTypes.static) {
      return {
        redirect: {
          destination: formatPathByLogicalGroupChannel(
            `/${channelPath}/${categoryCode}/plan`
          ),
          permanent: false,
        },
      };
    }
  } catch (error) {
    const isUnknownError = checkUnknownError(error);

    if (!isUnknownError) {
      const landingPagePath = formatPathByLogicalGroupChannel(
        `/${channelPath}`
      );
      return {
        redirect: {
          destination: `/${locale}${landingPagePath}`,
          permanent: false,
        },
      };
    }

    throw error;
  }

  const banner: BannerType = {
    title: productCategory.displayName,
    desktopImage: productCategory.banner?.desktopUrl || null,
    mobileImage: productCategory.banner?.mobileUrl || null,
  };

  return {
    props: {
      channelCode: channelConfig.code,
      channelPath,
      categoryCode,
      banner,
      entryForm: productCategory.entryForm || null,
      contentCode: productCategory.contentCode || null,
      channelConfig,
      title: productCategory?.contentTitle?.[locale] || null,
      description: productCategory?.contentDescription?.[locale] || null,
      ...(await serverSideTranslations(context.locale, [
        "entry-form",
        "product-category",
        "common",
        "cookie-consent",
        "main-page",
        "plan-page",
        "layout",
        "otp-form",
        "form",
        "category-content-droplead",
        "category-content-entry-form",
        "error-dialog",
      ])),
    },
  };
};

export default withUserOrGuest(withLayout(ProductCategoryPage));
